/**
 * Image optimization utilities using ImageKit.io for WebP conversion and CDN delivery
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "";

/**
 * ImageKit.io configuration
 * Using URL endpoint with web proxy for external images
 */
const IMAGEKIT_ENDPOINT = "https://ik.imagekit.io/h3s9gmqpu";

/**
 * Generate an ImageKit-optimized URL for any image
 * 
 * @param url - The original image URL (can be external or internal)
 * @param width - Optional width for resizing
 * @param height - Optional height for resizing
 * @returns ImageKit-optimized URL with WebP format and auto-focus
 * 
 * @example
 * ik("https://example.com/image.jpg", 400, 400)
 * // Returns: https://ik.imagekit.io/h3s9gmqpu/bortal/tr:w-400,h-400,f-webp,fo-auto/https://example.com/image.jpg
 */
export const ik = (
  url: string | null | undefined,
  width?: number,
  height?: number
): string | null => {
  if (!url) return null;
  
  // Skip optimization for data URLs
  if (url.startsWith("data:")) {
    return url;
  }
  
  // Build transformation string
  const transforms: string[] = [];
  if (width) transforms.push(`w-${width}`);
  if (height) transforms.push(`h-${height}`);
  transforms.push("f-webp", "fo-auto");
  
  const transformString = transforms.join(",");
  
  return `${IMAGEKIT_ENDPOINT}/tr:${transformString}/${url}`;
};

/**
 * Generate srcset for responsive images using ImageKit
 */
export const ikSrcSet = (
  url: string | null | undefined,
  widths: number[],
  aspectRatio?: number
): string => {
  if (!url) return "";
  
  return widths
    .map(w => {
      const h = aspectRatio ? Math.round(w / aspectRatio) : undefined;
      const optimized = ik(url, w, h);
      return optimized ? `${optimized} ${w}w` : "";
    })
    .filter(Boolean)
    .join(", ");
};

/**
 * Known external image domains that we should proxy for optimization
 */
const EXTERNAL_IMAGE_DOMAINS = [
  "media.pitchfork.com",
  "s.yimg.com",
  "upload.wikimedia.org",
  "cdn.craft.cloud",
  "i.guim.co.uk",
  "static.stereogum.com",
  "cdn.mos.cms.futurecdn.net",
  "consequence.net",
  "thefader-res.cloudinary.com",
  "www.thelineofbestfit.com",
  "diymag.com",
  "static.factmag.com",
  "nme.com",
  "www.rollingstone.com",
  "images.hellogiggles.com",
  "d94thh4m1x8qv.cloudfront.net",
];

/**
 * Check if a URL is from an external domain that should be proxied
 */
export const isExternalImageUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return EXTERNAL_IMAGE_DOMAINS.some(domain => 
      parsed.hostname.includes(domain)
    );
  } catch {
    return false;
  }
};

/**
 * Check if URL is from Supabase storage
 */
export const isSupabaseStorageUrl = (url: string): boolean => {
  return url.includes("supabase") && url.includes("/storage/");
};

/**
 * Get a proxied image URL for external images with caching
 */
export const getProxiedImageUrl = (
  url: string,
  options?: { width?: number; height?: number; quality?: number }
): string => {
  if (!url || !SUPABASE_URL) return url;
  
  // Only proxy external domains
  if (!isExternalImageUrl(url)) return url;
  
  const params = new URLSearchParams();
  params.set("url", url);
  if (options?.width) params.set("w", options.width.toString());
  if (options?.height) params.set("h", options.height.toString());
  if (options?.quality) params.set("q", (options.quality || 80).toString());
  
  return `${SUPABASE_URL}/functions/v1/image-proxy?${params.toString()}`;
};

/**
 * Get an optimized image URL with WebP format transformation
 * - For Supabase Storage URLs: use built-in transforms
 * - For external URLs: use the image proxy for caching
 */
export const getOptimizedImageUrl = (
  url: string | null | undefined,
  options?: {
    width?: number;
    height?: number;
    quality?: number;
  }
): string | null => {
  if (!url) return null;

  // Skip optimization for data URLs
  if (url.startsWith("data:")) {
    return url;
  }

  // For Supabase storage URLs, use built-in transforms
  if (isSupabaseStorageUrl(url)) {
    const params = new URLSearchParams();
    
    if (options?.width) params.set("width", options.width.toString());
    if (options?.height) params.set("height", options.height.toString());
    params.set("quality", (options?.quality || 80).toString());
    params.set("format", "webp");

    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${params.toString()}`;
  }

  // For known external domains, use the image proxy for caching
  if (isExternalImageUrl(url)) {
    return getProxiedImageUrl(url, options);
  }

  // For unknown URLs, return as-is
  return url;
};

/**
 * Generate srcset for responsive images
 */
export const generateSrcSet = (
  url: string | null | undefined,
  sizes: number[],
  quality?: number
): string => {
  if (!url) return "";
  
  return sizes
    .map(size => {
      const optimized = getOptimizedImageUrl(url, { width: size, quality });
      return optimized ? `${optimized} ${size}w` : "";
    })
    .filter(Boolean)
    .join(", ");
};

/**
 * Generate sizes attribute for responsive images
 */
export const generateSizes = (breakpoints: { [key: string]: string }): string => {
  return Object.entries(breakpoints)
    .map(([query, size]) => (query === "default" ? size : `(${query}) ${size}`))
    .join(", ");
};

/**
 * Default image sizes for common use cases
 */
export const IMAGE_SIZES = {
  thumbnail: { width: 100, height: 100, quality: 70 },
  avatar: { width: 200, height: 200, quality: 80 },
  card: { width: 304, height: 304, quality: 80 },
  cardMd: { width: 456, height: 456, quality: 80 },
  newsThumb: { width: 96, height: 96, quality: 75 },
  newsCard: { width: 384, height: 216, quality: 80 },
  profile: { width: 600, height: 600, quality: 85 },
  hero: { width: 800, height: 800, quality: 85 },
} as const;

/**
 * Responsive srcset breakpoints for artist cards
 */
export const ARTIST_CARD_SRCSET_SIZES = [152, 228, 304, 456];
export const ARTIST_CARD_SIZES = "(min-width: 768px) 25vw, 50vw";

/**
 * Check if the browser supports WebP
 */
export const supportsWebP = (): boolean => {
  if (typeof window === "undefined") return true;
  
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  return canvas.toDataURL("image/webp").startsWith("data:image/webp");
};
